import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, Truck, PackagePlus, MapPin, CheckCircle, Clock, Building2 } from 'lucide-react';
import apiClient from '../../utils/api';

export const DispatchDashboard: React.FC = () => {
  const [shipments, setShipments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.listShipments({}, 100, 0)
      .then(r => {
        const d = r.data.data;
        setShipments(d?.shipments ?? d?.rows ?? (Array.isArray(d) ? d : []));
      }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const confirmed  = shipments.filter(s => s.status === 'CONFIRMED').length;
  const departed   = shipments.filter(s => s.status === 'DEPARTED').length;
  const inTransit  = shipments.filter(s => s.status === 'IN_TRANSIT').length;
  const pending    = shipments.filter(s => s.status === 'PENDING').length;

  const stats = [
    { label: 'Awaiting Dispatch', value: confirmed, color: '#6366f1', icon: <Clock size={20} />,   link: '/dispatch/shipments?status=CONFIRMED' },
    { label: 'Departed',          value: departed,  color: '#f59e0b', icon: <Truck size={20} />,   link: '/dispatch/shipments?status=DEPARTED' },
    { label: 'In Transit',        value: inTransit, color: '#0ea5e9', icon: <Package size={20} />, link: '/dispatch/shipments?status=IN_TRANSIT' },
    { label: 'Pending Confirm',   value: pending,   color: '#94a3b8', icon: <CheckCircle size={20} />, link: '/dispatch/shipments?status=PENDING' },
  ];

  const readyToDispatch = shipments.filter(s => s.status === 'CONFIRMED');

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Dispatch Dashboard</h1>
        <Link to="/dispatch/transporters" className="btn btn-ghost"><Truck size={15} /> Transporters</Link>
      </div>

      {loading ? <div className="loading-spinner">Loading...</div> : (
        <>
          <div className="stats-grid">
            {stats.map(s => (
              <Link to={s.link} key={s.label} style={{ textDecoration: 'none' }}>
                <div className="stat-card" style={{ borderLeft: `4px solid ${s.color}`, cursor: 'pointer' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                      <div className="stat-label">{s.label}</div>
                      <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
                    </div>
                    <div style={{ color: s.color, opacity: 0.6 }}>{s.icon}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {readyToDispatch.length > 0 && (
            <div className="card">
              <div className="page-header" style={{ marginBottom: 16 }}>
                <h2 style={{ fontSize: 15, fontWeight: 600 }}>Ready to Dispatch ({readyToDispatch.length})</h2>
                <Link to="/dispatch/shipments?status=CONFIRMED" className="btn btn-ghost" style={{ fontSize: 12 }}>View all</Link>
              </div>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Tracking #</th>
                      <th>Company</th>
                      <th>Sender</th>
                      <th>Receiver</th>
                      <th>Destination</th>
                      <th>Amount</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {readyToDispatch.slice(0, 10).map((s: any) => (
                      <tr key={s.id}>
                        <td><code style={{ fontSize: 11 }}>{s.trackingNumber}</code></td>
                        <td style={{ fontSize: 12, color: '#6366f1', fontWeight: 500 }}>
                          <Building2 size={12} style={{ display: 'inline', marginRight: 4 }} />
                          {s.company?.name ?? '—'}
                        </td>
                        <td>{s.senderName}</td>
                        <td>{s.receiverName}</td>
                        <td>{s.receiverCity || '—'}</td>
                        <td>{Number(s.totalAmount).toLocaleString()} RWF</td>
                        <td>
                          <Link to={`/dispatch/shipments/${s.id}/dispatch`} className="btn btn-primary" style={{ padding: '4px 10px', fontSize: 12 }}>
                            <Truck size={13} /> Dispatch
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="card" style={{ marginTop: 20 }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Quick Actions</h2>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {[
                { label: 'All Shipments',  to: '/dispatch/shipments',    icon: <Package size={15} /> },
                { label: 'Transporters',   to: '/dispatch/transporters', icon: <Truck size={15} /> },
                { label: 'Trip Manifest',  to: '/dispatch/manifest',     icon: <PackagePlus size={15} /> },
                { label: 'Track Shipment', to: '/dispatch/tracking',     icon: <MapPin size={15} /> },
              ].map(a => (
                <Link key={a.label} to={a.to} className="btn btn-ghost">{a.icon} {a.label}</Link>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
};
